import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useGlobalAnalytics } from '@/hooks/useGlobalAnalytics';
import { useInitialSync } from '@/hooks/useInitialSync';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { fadeIn, transitions } from '@/lib/motion-config';

const AnalyticsDashboard = lazy(() =>
  import('@/components/features/analytics/AnalyticsDashboard').then((m) => ({
    default: m.AnalyticsDashboard,
  })),
);

export const Route = createFileRoute('/analytics/')({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { t } = useTranslation('common');
  const { analytics, isLoading, error } = useGlobalAnalytics(t);
  const { isSyncing } = useInitialSync();

  useDocumentTitle(
    `${t('analytics.header.title', 'Game analytics')} - ${t('app.name', 'One Score')}`,
  );

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <Suspense fallback={<Skeleton className="h-screen w-full" />}>
      <motion.div
        key={routeKey}
        variants={fadeIn}
        initial={shouldAnimate ? 'initial' : false}
        animate="animate"
        transition={transitions.springGentle}
      >
        <AnalyticsDashboard
          analytics={analytics}
          isLoading={isLoading || isSyncing}
          error={error}
        />
      </motion.div>
    </Suspense>
  );
}
